import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import ColorInput from 'components/field/ColorInput';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TemplateSelector from 'components/field/TemplateSelector';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import { addressVerification1DefaultValues } from 'components/templates/AddressVerification1';
import { addressVerification2DefaultValues } from 'components/templates/AddressVerification2';
import { deliverySlotSelectionDefaultValues } from 'components/templates/DeliverySlotSelection';
import { feedback1DefaultValues } from 'components/templates/Feedback1';
import { feedback2DefaultValues } from 'components/templates/Feedback2';
import { feedback3DefaultValues } from 'components/templates/Feedback3';
import { orderConfirmationDefaultValues } from 'components/templates/OrderConfirmation';
import { orderTracking1DefaultValues } from 'components/templates/OrderTracking1';
import { orderTracking2DefaultValues } from 'components/templates/OrderTracking2';
import { rescheduleDefaultValues } from 'components/templates/Reschedule';
import { returnRequest1DefaultValues } from 'components/templates/ReturnRequest1';
import { returnRequest2DefaultValues } from 'components/templates/ReturnRequest2';
import { SmsLayout, defaultValues } from 'components/templates/SmsLayout';
import { undeliveredAttempt1DefaultValues } from 'components/templates/UndeliveredAttempt1';
import { undeliveredAttempt2DefaultValues } from 'components/templates/UndeliveredAttempt2';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import { AUTOCOMPLETE, BRAND, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faDesktop, faMobile, faUndoAlt } from '@fortawesome/pro-regular-svg-icons';
import { faEye } from '@fortawesome/pro-solid-svg-icons';

import BrandForm from '../Brands/BrandForm';
import * as S from '../SMS/SMSSetting.styled';

const SmsComponents = {
	trackingSms: {
		template1: orderTracking1DefaultValues,
		template2: orderTracking2DefaultValues
	},
	addressVerificationSms: {
		template1: addressVerification1DefaultValues,
		template2: addressVerification2DefaultValues
	},
	deliverySlotSms: {
		template1: deliverySlotSelectionDefaultValues
	},
	feedbackSms: {
		template1: feedback1DefaultValues,
		template2: feedback2DefaultValues,
		template3: feedback3DefaultValues
	},

	undeliveredAttemptSms: {
		template1: undeliveredAttempt1DefaultValues,
		template2: undeliveredAttempt2DefaultValues
	},
	returnRequestSMS: {
		template1: returnRequest1DefaultValues,
		template2: returnRequest2DefaultValues
	},
	orderConfirmationSms: {
		template1: orderConfirmationDefaultValues
	},
	rescheduleSms: {
		template1: rescheduleDefaultValues
	}
};

const Templates = {
	trackingSms: {
		keywords: 'trackingSmsTextDescription',
		smsPath: '/sms/order-tracking',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/tracking-1.png',
				name: 'template1'
			},
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/tracking-2.png',
				name: 'template2'
			}
		]
	},
	feedbackSms: {
		keywords: 'feedbackSmsTextDescription',
		smsPath: '/sms/feedback',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/feedback-1.png',
				name: 'template1'
			},
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/feedback-2.png',
				name: 'template2'
			},
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/feedback-v2.png',
				name: 'template3'
			}
		]
	},

	addressVerificationSms: {
		keywords: 'addressVerificationSmsTextDescription',
		smsPath: '/sms/address-verification',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/addressVerification-1.png',
				name: 'template1'
			},
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/addressVerification-2.png',
				name: 'template2'
			}
		]
	},
	deliverySlotSms: {
		keywords: 'deliverySlotSelectionSMSTextDescription',
		smsPath: '/sms/delivery-slot',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/delivery-slot-1.png',
				name: 'template1'
			}
		]
	},
	undeliveredAttemptSms: {
		keywords: 'undeliveredSMSTextDescription',
		smsPath: '/sms/undelivered-attempt',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/undeliveredAttempt-1.png',
				name: 'template1'
			},
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/undeliveredAttempt-2.png',
				name: 'template2'
			}
		]
	},
	returnRequestSMS: {
		keywords: 'returnSmsTextDescription',
		smsPath: '/sms/return-request',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/returnRequest-1.png',
				name: 'template1'
			},
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/returnRequest-2.png',
				name: 'template2'
			}
		]
	},
	orderConfirmationSms: {
		keywords: 'orderConfirmationSmsTextDescription',
		smsPath: '/sms/order-confirmation',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/orderConfirmation2.png',
				name: 'template1'
			}
		]
	},
	rescheduleSms: {
		keywords: 'rescheduleSMSTextDescription',
		smsPath: '/sms/reschedule',
		templates: [
			{
				image: 'https://storage.googleapis.com/tryoto-public/templates/undeliveredAttempt-1.png',
				name: 'template1'
			}
		]
	}
};

const CustomizablePageForm = NiceModal.create(({ id, mode, modalName }) => {
	// Hooks and states declaration
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const isRestricted = useRestriction();
	const isAuthorized = useAccessControl();
	const { LanguageCode } = useLocale();
	const { closeModal } = useModalRouter();
	const { showError, showSuccess } = useAlert();
	const { track } = useAppTracking();
	const isMultiBrand = isAuthorized('BrandingModule');
	const [isMinimizing, setIsMinimizing] = useState(false);
	const navigate = useNavigate();

	const largeDefaultColSpan = {
		md: 12
	};
	const defaultColSpan = {
		md: 6
	};

	const fillForm = useCallback(async () => {
		const defaultValues = {
			smsType: isMultiBrand ? null : 'trackingSms',
			brandID: null,
			templatePropJSON: null,
			templateName: null,
			language: LanguageCode ?? null
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axios.post(BRAND.GET_BRANDED_PAGE, {
				ID: id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			const templatePropJSON = JSON.parse(data.templatePropJSON);

			return {
				...defaultValues,
				...data,
				templatePropJSON: templatePropJSON.propJson,
				templateName: templatePropJSON.templateName
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					smsType: yup.string().nullable().required(),
					brandID: yup.number().nullable(),
					templateName: yup.string().nullable(),
					language: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	// Form setup using react-hook-form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);
	const smsType = watch('smsType');
	const selectedTemplateData = Templates[smsType];

	const smsTypes = useMemo(() => {
		const types = ['trackingSms', 'feedbackSms', 'undeliveredAttemptSms', 'deliverySlotSms']; // Starter package SMS types

		if (isAuthorized('ScalePackage') || isAuthorized('EnterprisePackage')) {
			types.push('returnRequestSMS');
		}

		if (isAuthorized('OMSModule')) {
			types.push('orderConfirmationSms');
		}

		if (isAuthorized('EnterprisePackage')) {
			types.push('addressVerificationSms');
			types.push('rescheduleSms');
		}

		return types;
	}, []);

	// Autocomplete columns and labels definition
	const brandAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const brandAutocompleteLabels = useMemo(() => ['Name'], []);

	const handleCloseModal = () => {
		closeModal();
	};

	const onSubmit = async (data) => {
		const URL = mode === 'edit' ? BRAND.UPDATE_CUSTOMIZABLE_PAGE : BRAND.ADD_CUSTOMIZABLE_PAGE;
		const body = {
			ID: id ?? undefined,
			...data,
			templatePropJSON:
				JSON.stringify({
					propJson: data.templatePropJSON,
					templateName: data.templateName
				}) ?? null
		};
		delete body.templateName;

		try {
			const response = await axios.post(URL, body);

			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.CUSTOMIZABLE_PAGES));
				handleCloseModal();
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.F010') {
				showError({
					message: 'errorMsg.sameTemplateAlreadyExist'
				});
			} else {
				AppLogger.error(error);
				showError();
			}
		}
	};

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-smsSetting"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={mode === 'edit' ? 'editCustomizablePages' : 'addCustomizablePages'}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'edit' ? 'editCustomizablePages' : 'addCustomizablePages'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="messageType" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							data={smsTypes}
							disabled={mode === 'edit' || !isMultiBrand}
							value={smsType}
							name="smsType"
							setValue={(value) => {
								setValue(`smsType`, value, { shouldValidate: true, shouldDirty: true });
								setValue('templateName', 'template1');
								setValue('templatePropJSON', SmsComponents[value].template1, {
									shouldDirty: true,
									shouldValidate: true
								});
							}}
						/>
					</FormItem>
					<FormItem
						label="brandName"
						colSpan={defaultColSpan}
						isLoading={isLoading}
						roleName="BrandingModule">
						<AutoComplete
							name="brandID"
							autoCompleteName={AUTOCOMPLETE.BRANDS}
							columns={brandAutocompleteColumns}
							selectionLabelAccessor={brandAutocompleteLabels}
							value={watch('brandID')}
							setValue={(value) => {
								setValue('brandID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							firstItem={
								isAuthorized('/autocomplete/brand/addANewBrand')
									? {
											action: () =>
												NiceModal.show(BrandForm, {
													setBrand: (value) => {
														setValue(`brandID`, value, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewBrand</Typography>,
											disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
											disabledTooltip: (
												<div style={{ display: 'block' }}>
													<ModuleToolTipContainer
														infoImage="lottie_multiBrand"
														title="multiBrandModule"
														description="multiBrandModuleSubtitle"
													/>

													<Button
														style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
														className="mt-3"
														label="showPlans"
														icon={faEye}
														variant="blue"
														onMouseDown={() => {
															track('moreThan3Brands');
															navigate(PATHS.OTO_MODULES);
														}}
													/>
												</div>
											)
										}
									: undefined
							}
						/>
					</FormItem>
					<FormItem label="language" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							enumName={ENUMS.LANGUAGE_CODES}
							value={watch(`language`)}
							setValue={(value) => {
								setValue(`language`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					{selectedTemplateData && (
						<FormItem label="customizablePageTemplates" toolTip="customizablePageTemplates.description">
							<F.TemplateSelectorContainer>
								{selectedTemplateData.templates.map((smsTemplate, index) => (
									<TemplateSelector
										key={index}
										name={smsTemplate.name}
										imageUrl={smsTemplate.image}
										className={watch('templateName') === smsTemplate.name ? 'selected' : ''}
										checkAction={() => {
											if (mode !== 'edit') {
												setValue('templatePropJSON', SmsComponents[smsType][smsTemplate.name]);
											}

											setValue('templateName', smsTemplate.name, {
												shouldDirty: true,
												shouldValidate: true
											});

											setValue('templatePropJSON', SmsComponents[smsType][smsTemplate.name], {
												shouldDirty: true,
												shouldValidate: true
											});
										}}
										configAction={() => {
											NiceModal.show(SMSTemplateEditForm, {
												smsPath: selectedTemplateData.smsPath,
												templateName: smsTemplate.name,
												smsType,
												mode,
												formMethods
											});
										}}
										disabled={!isMultiBrand && index % 2 === 1}
										isConfigurable={
											isMultiBrand && watch('templateName') === smsTemplate.name ? 'selected' : ''
										}
									/>
								))}
							</F.TemplateSelectorContainer>
						</FormItem>
					)}

					<FormItem colSpan={largeDefaultColSpan}>
						<Alert content="customizablePage.description" showIcon />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label={mode === 'edit' ? 'save' : 'add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					iconPos="right"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});
export default CustomizablePageForm;

const SMSTemplateEditForm = NiceModal.create(({ smsPath, templateName, smsType, mode, formMethods }) => {
	const { t } = useTranslation();

	const modal = useModal();
	const [isMobile, setIsMobile] = useState(true);

	const { control, setValue } = formMethods;

	const watchedFields = useWatch({
		control,
		name: 'templatePropJSON'
	});

	const setDefaultProp = (smsType, templateName) => {
		setValue('templatePropJSON', SmsComponents[smsType][templateName], {
			shouldDirty: true,
			shouldValidate: true
		});
	};

	const templatePropJSON = { propJson: watchedFields };
	const data = { templatePropJSON, ...defaultValues };

	const currentTemplateProps = SmsComponents[smsType] && SmsComponents[smsType][templateName];

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-smsTemplateEdit">
			<ModalTitle>
				<Typography variant="p1">
					{t(smsType)} - {t(templateName)}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<S.Container>
						<S.IframeContainer>
							<S.IframeHeader>
								<Button
									onClick={() => {
										setIsMobile(true);
									}}
									size="small"
									variant="action"
									icon={faMobile}
									label={'mobile'}
									className={isMobile ? 'active' : ''}
								/>
								<Button
									onClick={() => {
										setIsMobile(false);
									}}
									size="small"
									variant="action"
									icon={faDesktop}
									label={'desktop'}
									className={!isMobile ? 'active' : ''}
								/>
							</S.IframeHeader>

							<S.IframeWrapper isMobile={isMobile}>
								<SmsLayout
									pathname={smsPath}
									templateName={templateName}
									data={data}
									isMobile={isMobile}
								/>
							</S.IframeWrapper>
						</S.IframeContainer>

						<S.ConfigContainer>
							<S.Header>
								<Typography noWrap variant="p1" fontWeight="500">
									templateSettings
								</Typography>
							</S.Header>

							{currentTemplateProps.map((item, index) => (
								<div key={index} className="row row-cols-1">
									{item.type === 'color' && (
										<FormItem
											label={item.name}
											direction="row-reverse"
											style={{
												padding: '0'
											}}>
											<ColorInput name={`templatePropJSON[${index}].value`} />
										</FormItem>
									)}

									{item.type === 'boolean' && (
										<FormItem
											label={item.name}
											direction="row-reverse"
											style={{
												padding: '0'
											}}>
											<SwitchInput name={`templatePropJSON[${index}].value`} />
										</FormItem>
									)}

									{item.type === 'select' && (
										<FormItem
											label={item.name}
											direction="column"
											style={{
												padding: '0'
											}}>
											<EnumSelect
												data={['nps', 'star']}
												value={templatePropJSON.propJson[index]?.value}
												setValue={(value) => {
													setValue(`templatePropJSON[${index}].value`, value, {
														shouldValidate: true,
														shouldDirty: true
													});
												}}
												placeholder="Select"
												searchable={false}
												valueRemovable={false}
											/>
										</FormItem>
									)}

									{item.type === 'input' && (
										<FormItem label={item.name} direction="column" style={{ padding: '0' }}>
											<StringInput name={`templatePropJSON[${index}].value`} />
										</FormItem>
									)}
								</div>
							))}
						</S.ConfigContainer>
					</S.Container>
				</Form>
			</ModalContent>
			<ModalActions>
				{mode === 'edit' && (
					<Button
						onClick={() => {
							setDefaultProp(smsType, templateName);
						}}
						icon={faUndoAlt}
						label="reset"
						size="x-small"
						variant="blackOutline"
					/>
				)}

				<Button
					label="OK"
					onClick={() => {
						modal.remove();
					}}
				/>
			</ModalActions>
		</Modal>
	);
});
